import React, { useEffect, useState } from "react";
import apiService from "../services/api";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import "dayjs/locale/en";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("en");

const Remainder = ({
  isOpen,
  onClose,
  anchorRef,
  notification,
  handleChangeLeadData,
  leadId,
  parsedNotificTime,
}) => {
  const [enabled, setEnabled] = useState(notification);
  // Normalize incoming time (assumed stored as UTC ISO) to user's local time for the picker
  const initialLocal = parsedNotificTime ? dayjs(parsedNotificTime).local() : null;
  const [selectedDate, setSelectedDate] = useState<any>(initialLocal);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  // timerId state removed - backend handles all reminder processing

  useEffect(() => {
    if (isOpen && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 15,
        left: rect.left + window.scrollX - 80 + rect.width / 2,
      });
    }
  }, [isOpen, anchorRef]);

  if (!isOpen) return null;

  // Client-side reminder logic removed - backend handles all reminder processing

  const handleSave = async () => {
    // Convert local pick to UTC ISO before persisting
    const utcIso = selectedDate ? dayjs(selectedDate).utc().toISOString() : null;

    // Persist to backend (so reminder is scheduled server-side)
    try {
      await (apiService as any).updateLead(leadId, {
        notification: enabled,
        parsedNotificTime: utcIso,
      });
    } catch (e) {
      console.warn('[Reminder] Failed to persist to backend', e);
    }

    // Update local UI state
    handleChangeLeadData(leadId, "notification", enabled);
    handleChangeLeadData(leadId, "parsedNotificTime", utcIso);

    // Backend handles all reminder processing and notifications
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="absolute bg-white rounded-xl p-6 w-[320px] shadow-lg border border-darkestSlate"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-h3 font-semibold">Outreach Reminder</h2>
          <div className="flex items-center justify-between">
            <Switch
              size="small"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              sx={{
                width: 42,
                height: 24,
                padding: "0px",
                "& .MuiSwitch-switchBase": {
                  padding: 0,
                  margin: "2px",
                  "&.Mui-checked": {
                    transform: "translateX(18px)",
                    color: "#fff",
                    "& + .MuiSwitch-track": {
                      backgroundColor: enabled ? "#407097" : "#CBD5E0",
                      opacity: 1,
                    },
                  },
                },
                "& .MuiSwitch-thumb": {
                  width: 20,
                  height: 20,
                },
                "& .MuiSwitch-track": {
                  borderRadius: 26 / 2,
                  backgroundColor: enabled ? "#407097" : "#CBD5E0",
                  opacity: 1,
                },
              }}
            />
          </div>
        </div>

        {enabled && (
          <div className="flex items-center justify-between mb-4">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                onChange={(newValue) => setSelectedDate(newValue as any)}
                value={selectedDate}
                minDateTime={dayjs()}
                slotProps={
                  {
                    desktopPaper: {
                      sx: {
                        mt: 1.25,
                        transform: "translateX(calc(0% - 30px))",
                        borderRadius: "12px",
                        border: "1px solid #323C48",
                        boxShadow: "box-shadow: 0 4px 24px #CBD5E066",
                        "& .Mui-selected": {
                          backgroundColor: "#407097 !important",
                          color: "#FFFFFF !important",
                        },
                      },
                    },
                    textField: {
                      placeholder: "Pick date & time",
                      sx: {
                        width: "100%",
                        "& .MuiInputLabel-root": {
                          display: "none",
                        },
                        "& .MuiOutlinedInput-root": {
                          fontFamily: "Inter, sans-serif",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#222830",
                          height: "40px",
                          borderRadius: "12px",
                          backgroundColor: "lightestGray",
                          display: "flex",
                          alignItems: "center",
                          "&.Mui-focused": {
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#407097",
                              borderWidth: "2px",
                            },
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#C3CBD5",
                          },
                        },
                        "& .MuiInputBase-input": {
                          padding: "0 12px",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#323C48",
                        },
                      },
                    },
                  } as any
                }
              />
            </LocalizationProvider>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              height: "40px",
              width: "96px",
              borderRadius: "100px",
              textTransform: "none",
              border: "1px solid #222830",
              color: "#222830",
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: "600",
              padding: "0 16px",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              height: "40px",
              width: "96px",
              borderRadius: "100px",
              textTransform: "none",
              backgroundColor: "#407097",
              color: "#FFFFFF",
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: "600",
              padding: "0 16px",
              "&:hover": {
                backgroundColor: "#407097",
                opacity: 0.9,
              },
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Remainder;
